import React, { useEffect, useState, useRef } from 'react';
import { useParams, useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { utils } from '@ohif/core';
import { ImageViewerProvider, DragAndDropProvider } from '@ohif/ui-next';
import { useSearchParams } from '../../hooks';
import { useAppConfig } from '@state';
import ViewportGrid from '@components/ViewportGrid';
import Compose from './Compose';
import loadModules from '../../pluginImports';
import { defaultRouteInit } from './defaultRouteInit';
import { updateAuthServiceAndCleanUrl } from './updateAuthServiceAndCleanUrl';
import { saveSession, loadSession, consumeResumeRequest } from '../../utils/sessionResume';

const { getSplitParam } = utils;

export default function ModeRoute({
  mode,
  dataSourceName,
  extensionManager,
  servicesManager,
  commandsManager,
  hotkeysManager,
}: withAppTypes) {
  const [appConfig] = useAppConfig();

  // Parse route params/querystring
  const location = useLocation();

  // The react router DOM placeholder map (see https://reactrouter.com/en/main/hooks/use-params).
  const params = useParams();
  // The URL's query search parameters where the keys casing is maintained
  const query = useSearchParams();

  const navigate = useNavigate();

  mode?.onModeInit?.({
    servicesManager,
    extensionManager,
    commandsManager,
    appConfig,
    query,
  });

  // The URL's query search parameters where the keys are all lower case.
  const lowerCaseSearchParams = useSearchParams({ lowerCaseKeys: true });

  const [studyInstanceUIDs, setStudyInstanceUIDs] = useState(null);

  const [refresh, setRefresh] = useState(false);
  const [ExtensionDependenciesLoaded, setExtensionDependenciesLoaded] = useState(false);

  const layoutTemplateData = useRef(false);
  const locationRef = useRef(null);
  const isMounted = useRef(false);

  if (location !== locationRef.current) {
    layoutTemplateData.current = null;
    locationRef.current = location;
  }

  const {
    displaySetService,
    panelService,
    hangingProtocolService,
    userAuthenticationService,
    customizationService,
  } = servicesManager.services;

  // ~ Session resume: while a study is open, snapshot the reading state (URL,
  // per-pane series / slice / W-L) every few seconds + on unload, so a crash or
  // tab-close can be resumed from the worklist. Restore only runs when the
  // worklist Resume pill set the one-shot flag — a normal open is never altered.
  useEffect(() => {
    if (!ExtensionDependenciesLoaded || !studyInstanceUIDs?.length) {
      return;
    }
    const services: any = servicesManager.services;
    const { viewportGridService, cornerstoneViewportService } = services;
    if (!viewportGridService) {
      return;
    }

    const capture = () => {
      try {
        const state = viewportGridService.getState();
        if (!state?.viewports?.size) {
          return;
        }
        const panes: any[] = [];
        state.viewports.forEach((v: any, viewportId: string) => {
          const series = (v.displaySetInstanceUIDs || [])
            .map((uid: string) => displaySetService.getDisplaySetByUID(uid)?.SeriesInstanceUID)
            .filter(Boolean);
          const pane: any = { series };
          try {
            const vp: any = cornerstoneViewportService?.getCornerstoneViewport(viewportId);
            const idx = vp?.getCurrentImageIdIndex?.();
            if (typeof idx === 'number' && idx >= 0) {
              pane.imageIndex = idx;
              // The exact slice's SOPInstanceUID — resolved from the shown image's
              // display set so the reopen can deep-link straight to it.
              try {
                const shownUid = v.displaySetInstanceUIDs?.[0];
                const ds: any = shownUid && displaySetService.getDisplaySetByUID(shownUid);
                const inst = ds?.instances?.[idx] || ds?.images?.[idx];
                if (inst?.SOPInstanceUID) {
                  pane.sopInstanceUID = inst.SOPInstanceUID;
                }
              } catch (e) {
                /* optional */
              }
            }
            const voiRange = vp?.getProperties?.()?.voiRange;
            if (voiRange && isFinite(voiRange.lower) && isFinite(voiRange.upper)) {
              pane.voiRange = { lower: voiRange.lower, upper: voiRange.upper };
            }
          } catch (e) {
            /* pane details are best-effort */
          }
          panes.push(pane);
        });
        // Patient context for the worklist pill.
        let patientName = '';
        let studyDescription = '';
        try {
          const anyDs: any = (displaySetService.getActiveDisplaySets() || [])[0];
          const inst = anyDs?.instances?.[0] || anyDs?.instance || {};
          const p = inst.PatientName;
          patientName = typeof p === 'string' ? p : p?.Alphabetic || '';
          studyDescription = inst.StudyDescription || '';
        } catch (e) {
          /* optional */
        }
        saveSession({
          url: window.location.pathname + window.location.search,
          studyUIDs: [...studyInstanceUIDs],
          patientName,
          studyDescription,
          panes,
        });
      } catch (e) {
        /* capture must never break the viewer */
      }
    };

    const interval = setInterval(capture, 4000);
    window.addEventListener('beforeunload', capture);

    // Explicit restore (worklist Resume pill): re-apply pane series/slice/W-L
    // once the grid has hydrated its display sets.
    let cancelled = false;
    if (consumeResumeRequest()) {
      const saved = loadSession();
      const sameStudies =
        saved &&
        saved.studyUIDs.length === studyInstanceUIDs.length &&
        saved.studyUIDs.every(uid => studyInstanceUIDs.includes(uid));
      if (sameStudies) {
        (async () => {
          // Wait for panes to receive display sets (hp applied + data arrived).
          for (let tries = 0; tries < 60 && !cancelled; tries++) {
            const st = viewportGridService.getState();
            const ready =
              st?.viewports?.size &&
              [...st.viewports.values()].some((v: any) => v.displaySetInstanceUIDs?.length);
            if (ready) {
              break;
            }
            await new Promise(r => setTimeout(r, 250));
          }
          if (cancelled) {
            return;
          }
          try {
            const st = viewportGridService.getState();
            const viewportIds = [...st.viewports.keys()];
            const activeDs: any[] = displaySetService.getActiveDisplaySets() || [];
            for (let i = 0; i < Math.min(viewportIds.length, saved.panes.length); i++) {
              if (cancelled) {
                return;
              }
              const viewportId = viewportIds[i];
              const pane = saved.panes[i];
              if (!pane?.series?.length) {
                continue;
              }
              // Re-select the same series if the pane currently shows another.
              const wantedUid = activeDs.find(
                d => d.SeriesInstanceUID === pane.series[0] && !d.isDerivedDisplaySet
              )?.displaySetInstanceUID;
              const shown = st.viewports.get(viewportId)?.displaySetInstanceUIDs || [];
              if (wantedUid && !shown.includes(wantedUid)) {
                try {
                  await viewportGridService.setDisplaySetsForViewport({
                    viewportId,
                    displaySetInstanceUIDs: [wantedUid],
                  });
                  await new Promise(r => setTimeout(r, 400));
                } catch (e) {
                  continue;
                }
              }
              try {
                const vp: any = cornerstoneViewportService?.getCornerstoneViewport(viewportId);
                // The PRIMARY pane's slice is deep-linked via the URL
                // (initialSOPInstanceUID → the hanging protocol opens AT it), so we
                // must NOT re-jump it here (that raced the HP and lost). Only extra
                // panes need a post-load jump. W/L is always applied (no URL param).
                const jumpThisPane = i > 0;
                if (jumpThisPane && vp && typeof pane.imageIndex === 'number') {
                  // The hanging protocol positions the viewport (default slice) AFTER
                  // the display set loads, and the stack itself loads progressively.
                  // Wait until BOTH the imageIds stop growing AND the current slice
                  // stops changing (HP finished) so our jump is the last word.
                  let stableCount = 0;
                  let prevLen = -1;
                  let prevIdx = -1;
                  for (let w = 0; w < 60 && !cancelled; w++) {
                    const len = vp.getImageIds?.()?.length || 0;
                    const idx = vp.getCurrentImageIdIndex?.() ?? -1;
                    if (len > 0 && len === prevLen && idx === prevIdx) {
                      stableCount++;
                      if (stableCount >= 2) {
                        break;
                      }
                    } else {
                      stableCount = 0;
                    }
                    prevLen = len;
                    prevIdx = idx;
                    await new Promise(r => setTimeout(r, 200));
                  }
                  const n = vp.getImageIds?.()?.length || 0;
                  if (n > 0 && !cancelled) {
                    const target = Math.max(0, Math.min(pane.imageIndex, n - 1));
                    commandsManager.run('jumpToImage', { imageIndex: target, viewport: { id: viewportId } });
                    // Verify it stuck; re-assert once if something nudged it.
                    await new Promise(r => setTimeout(r, 300));
                    if (!cancelled && vp.getCurrentImageIdIndex?.() !== target) {
                      commandsManager.run('jumpToImage', { imageIndex: target, viewport: { id: viewportId } });
                    }
                  }
                }
                if (vp && pane.voiRange && !cancelled) {
                  vp.setProperties?.({ voiRange: pane.voiRange });
                  vp.render?.();
                }
              } catch (e) {
                /* per-pane restore is best-effort */
              }
            }
          } catch (e) {
            /* restore must never break the load */
          }
        })();
      }
    }

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('beforeunload', capture);
      capture(); // final snapshot when leaving the route normally
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyInstanceUIDs, ExtensionDependenciesLoaded]);

  const { extensions, sopClassHandlers, hangingProtocol } = mode;

  const runTimeHangingProtocolId = lowerCaseSearchParams.get('hangingprotocolid');
  const runTimeStageId = lowerCaseSearchParams.get('stageid');
  const token = lowerCaseSearchParams.get('token');

  if (token) {
    updateAuthServiceAndCleanUrl(token, location, userAuthenticationService);
  }

  // An undefined dataSourceName implies that the active data source that is already set in the ExtensionManager should be used.
  if (dataSourceName !== undefined) {
    extensionManager.setActiveDataSource(dataSourceName);
  }

  const dataSource = extensionManager.getActiveDataSourceOrNull();

  // Only handling one route per mode for now
  const route = mode.routes?.[0] ?? null;

  useEffect(() => {
    const loadExtensions = async () => {
      const loadedExtensions = await loadModules(Object.keys(extensions));
      for (const extension of loadedExtensions) {
        const { id: extensionId } = extension;
        if (extensionManager.registeredExtensionIds.indexOf(extensionId) === -1) {
          await extensionManager.registerExtension(extension);
        }
      }

      if (isMounted.current) {
        setExtensionDependenciesLoaded(true);
      }
    };

    loadExtensions();
  }, []);

  useEffect(() => {
    // Preventing state update for unmounted component
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!ExtensionDependenciesLoaded) {
      return;
    }

    // Todo: this should not be here, data source should not care about params
    const initializeDataSource = async (params, query) => {
      await dataSource.initialize({
        params,
        query,
      });
      setStudyInstanceUIDs(dataSource.getStudyInstanceUIDs({ params, query }));
    };

    initializeDataSource(params, query);
    return () => {
      layoutTemplateData.current = null;
    };
  }, [location, ExtensionDependenciesLoaded]);

  /**
   * Validates study existence before loading the viewer.
   * Moved from PanelStudyBrowser.tsx to ensure validation runs in all modes
   */
  useEffect(() => {
    if (!ExtensionDependenciesLoaded || !studyInstanceUIDs?.length || !dataSource) {
      return;
    }

    const validateStudies = async () => {
      for (const studyInstanceUID of studyInstanceUIDs) {
        try {
          const qidoForStudyUID = await dataSource.query.studies.search({
            studyInstanceUid: studyInstanceUID,
          });

          if (!qidoForStudyUID?.length) {
            console.warn('Study not found:', studyInstanceUID);
            navigate('/notfoundstudy');
            return;
          }
        } catch (error) {
          console.error('Error validating study:', studyInstanceUID, error);
          navigate('/notfoundstudy');
          return;
        }
      }
    };

    validateStudies();
  }, [studyInstanceUIDs, ExtensionDependenciesLoaded, dataSource, navigate]);

  useEffect(() => {
    if (!ExtensionDependenciesLoaded || !studyInstanceUIDs?.length) {
      return;
    }

    const retrieveLayoutData = async () => {
      const layoutData = await route.layoutTemplate({
        location,
        servicesManager,
        studyInstanceUIDs,
      });

      if (isMounted.current) {
        const { leftPanels = [], rightPanels = [], ...layoutProps } = layoutData.props;

        panelService.reset();
        panelService.addPanels(panelService.PanelPosition.Left, leftPanels);
        panelService.addPanels(panelService.PanelPosition.Right, rightPanels);

        // layoutProps contains all props but leftPanels and rightPanels
        layoutData.props = layoutProps;

        layoutTemplateData.current = layoutData;
        setRefresh(!refresh);
      }
    };
    if (Array.isArray(studyInstanceUIDs) && studyInstanceUIDs[0]) {
      retrieveLayoutData();
    }
    return () => {
      layoutTemplateData.current = null;
    };
  }, [studyInstanceUIDs, ExtensionDependenciesLoaded]);

  useEffect(() => {
    if (!layoutTemplateData.current || !ExtensionDependenciesLoaded || !studyInstanceUIDs?.length) {
      return;
    }

    const setupRouteInit = async () => {
      // TODO: For some reason this is running before the Providers
      // are calling setServiceImplementation
      // TODO -> iterate through services.

      // Extension

      // Add SOPClassHandlers to a new SOPClassManager.
      displaySetService.init(extensionManager, sopClassHandlers);

      extensionManager.onModeEnter({
        servicesManager,
        extensionManager,
        commandsManager,
        appConfig,
      });

      // use the URL hangingProtocolId if it exists, otherwise use the one
      // defined in the mode configuration
      let hangingProtocolIdToUse = hangingProtocolService.getProtocolById(runTimeHangingProtocolId)
        ? runTimeHangingProtocolId
        : hangingProtocol;

      // A comparison protocol (e.g. @ohif/hpCompare) references priors and lays out a
      // multi-study grid. If it is requested — e.g. carried over as a sticky URL param
      // from a previous compare — but only a single study is open, it would render an
      // N-up layout with empty/black panes. Fall back to the mode default in that case.
      const resolvedProtocol =
        typeof hangingProtocolIdToUse === 'string'
          ? hangingProtocolService.getProtocolById(hangingProtocolIdToUse)
          : null;
      const priorsNeeded = resolvedProtocol?.numberOfPriorsReferenced || 0;
      if (priorsNeeded > 0 && (studyInstanceUIDs?.length || 0) < priorsNeeded + 1) {
        hangingProtocolIdToUse = hangingProtocol;
      }

      // Determine the index of the stageId if the hangingProtocolIdToUse is defined
      const stageIndex = Array.isArray(hangingProtocolIdToUse)
        ? -1
        : hangingProtocolService.getStageIndex(hangingProtocolIdToUse, {
            stageId: runTimeStageId || undefined,
          });
      // Ensure that the stage index is never negative
      // If stageIndex is negative (e.g., if stage wasn't found), use 0 as the default
      const stageIndexToUse = Math.max(0, stageIndex);

      // Sets the active hanging protocols - if hangingProtocol is undefined,
      // resets to default.  Done before the onModeEnter to allow the onModeEnter
      // to perform custom hanging protocol actions
      hangingProtocolService.setActiveProtocolIds(hangingProtocolIdToUse);

      mode?.onModeEnter({
        servicesManager,
        extensionManager,
        commandsManager,
        appConfig,
      });

      // Move hotkeys setup here, after onModeEnter
      const hotkeys = customizationService.getCustomization('ohif.hotkeyBindings');
      hotkeysManager.setDefaultHotKeys(hotkeys);

      /**
       * The next line should get all the query parameters provided by the URL
       * - except the StudyInstanceUIDs - and create an object called filters
       * used to filtering the study as the user wants otherwise it will return
       * a empty object.
       *
       * Example:
       * const filters = {
       *   seriesInstanceUID: 1.2.276.0.7230010.3.1.3.1791068887.5412.1620253993.114611
       * }
       */
      const filters =
        Array.from(query.keys()).reduce((acc: Record<string, string>, val: string) => {
          const lowerVal = val.toLowerCase();
          // Not sure why the case matters here - it doesn't in the URL
          if (lowerVal === 'seriesinstanceuids' || lowerVal === 'seriesinstanceuid') {
            const seriesUIDs = getSplitParam(lowerVal, query);
            return {
              ...acc,
              seriesInstanceUID: seriesUIDs,
            };
          }
          return { ...acc, [val]: getSplitParam(lowerVal, query) };
        }, {}) ?? {};

      let unsubs;

      if (route.init) {
        unsubs = await route.init(
          {
            servicesManager,
            extensionManager,
            hotkeysManager,
            studyInstanceUIDs,
            dataSource,
            filters,
          },
          hangingProtocolIdToUse,
          stageIndexToUse
        );
      }

      return defaultRouteInit(
        {
          servicesManager,
          studyInstanceUIDs,
          dataSource,
          filters,
          appConfig,
        },
        hangingProtocolIdToUse,
        stageIndexToUse
      );
    };

    let unsubscriptions;
    setupRouteInit().then(unsubs => {
      unsubscriptions = unsubs;

      mode?.onSetupRouteComplete?.({
        servicesManager,
        extensionManager,
        commandsManager,
      });
    });

    return () => {
      // The mode.onModeExit must be done first to allow it to store
      // information, and must be in a try/catch to ensure subscriptions
      // are unsubscribed.
      try {
        mode?.onModeExit?.({
          servicesManager,
          extensionManager,
          appConfig,
        });
      } catch (e) {
        console.warn('mode exit failure', e);
      }
      // Clean up hotkeys
      hotkeysManager.destroy();

      // The unsubscriptions must occur before the extension onModeExit
      // in order to prevent exceptions during cleanup caused by spurious events
      if (unsubscriptions) {
        unsubscriptions.forEach(unsub => {
          unsub();
        });
      }
      // The extension manager must be called after the mode, this is
      // expected to cleanup the state to a standard setup.
      extensionManager.onModeExit();
    };
  }, [
    mode,
    dataSourceName,
    location,
    ExtensionDependenciesLoaded,
    route,
    servicesManager,
    extensionManager,
    hotkeysManager,
    studyInstanceUIDs,
    refresh,
  ]);

  if (!studyInstanceUIDs || !layoutTemplateData.current || !ExtensionDependenciesLoaded) {
    return null;
  }

  const ViewportGridWithDataSource = props => {
    return ViewportGrid({ ...props, dataSource });
  };

  const CombinedExtensionsContextProvider = createCombinedContextProvider(
    extensionManager,
    servicesManager,
    commandsManager
  );

  const getLayoutComponent = props => {
    const layoutTemplateModuleEntry = extensionManager.getModuleEntry(
      layoutTemplateData.current.id
    );
    const LayoutComponent = layoutTemplateModuleEntry.component;

    return <LayoutComponent {...props} />;
  };

  const LayoutComponent = getLayoutComponent({
    ...layoutTemplateData.current.props,
    ViewportGridComp: ViewportGridWithDataSource,
  });

  return (
    <ImageViewerProvider StudyInstanceUIDs={studyInstanceUIDs}>
      {CombinedExtensionsContextProvider ? (
        <CombinedExtensionsContextProvider>
          <DragAndDropProvider>{LayoutComponent}</DragAndDropProvider>
        </CombinedExtensionsContextProvider>
      ) : (
        <DragAndDropProvider>{LayoutComponent}</DragAndDropProvider>
      )}
    </ImageViewerProvider>
  );
}

/**
 * Creates a combined context provider using the context modules from the extension manager.
 * @param {object} extensionManager - The extension manager instance.
 * @param {object} servicesManager - The services manager instance.
 * @param {object} commandsManager - The commands manager instance.
 * @returns {React.Component} - A React component that provides combined contexts to its children.
 */
function createCombinedContextProvider(extensionManager, servicesManager, commandsManager) {
  const extensionsContextModules = extensionManager.getModulesByType(
    extensionManager.constructor.MODULE_TYPES.CONTEXT
  );

  if (!extensionsContextModules?.length) {
    return;
  }

  const contextModuleProviders = extensionsContextModules.flatMap(({ module }) => {
    return module.map(aContextModule => {
      return aContextModule.provider;
    });
  });

  return ({ children }) => {
    return Compose({ components: contextModuleProviders, children });
  };
}

ModeRoute.propTypes = {
  mode: PropTypes.object.isRequired,
  dataSourceName: PropTypes.string,
  extensionManager: PropTypes.object,
  servicesManager: PropTypes.object,
  hotkeysManager: PropTypes.object,
  commandsManager: PropTypes.object,
};
